"use strict";

var $ = require('dibs-jquery-shim');
var _ = require('underscore');

/**
 * @class Proxier is a class that takes a target object as a parameter and returns another object
 *        that proxies/delegates any references to it to the target object. This is useful for
 *        instances like the cart that has an implementation for when a user is logged off and
 *        another for when they are logged in (see ProxiedCartCollection).
 *
 *  Sample Usage:
 *      var someModel = new Backbone.Model({ text: 'this belongs to someModel' });
 *      var anotherModel = new Backbone.Model({ text: 'this is anotherModel'});
 *      var proxiedObj = new Proxier(someModel);
 *      proxiedObj.get('text'); // "this belongs to someModel"
 *      proxiedObj.__proxySwitch(anotherModel);
 *      proxiedObj.get('text'); // "this is anotherModel"
 */

var PROXIER_PROPERTIES = ['__proxySwitch', '__preserveEvents', '__forcePrototype'];
var FORCE_PROXY_PROPS = ['_events', '_listenerId', '_listenId']; // These are not necessarily defined at proxy time

// only in underscore 1.8
var allKeys = obj => {
    var keys = [];
    if (!_.isObject(obj)) {
        return [];
    }
    for (var key in obj) { // eslint-disable-line
        keys.push(key);
    }
    return keys;
};

function bindProxy(obj, target, forcePrototype) {
    var proxy;
    var proxiedKeys = _.union(allKeys(target), FORCE_PROXY_PROPS);
    if (obj) {
        proxy = obj;
        _.each(allKeys(proxy), key => {
            if (!_.contains(PROXIER_PROPERTIES, key)) {
                delete proxy[key];
            }
        });
        // Computationally expensive, has to be forced
        if (Object.setPrototypeOf && forcePrototype) {
            Object.setPrototypeOf(proxy, Object.getPrototypeOf(target));
        }
    } else {
        proxy = Object.create(Object.getPrototypeOf(target)); // JS optimizer friendly
    }
    _.each(proxiedKeys, key => {
        Object.defineProperty(proxy, key, {
            configurable: true,
            get: () => {
                return _.isFunction(target[key]) ?
                    $.proxy(target[key], target) : target[key];
            },
            set: val => target[key] = val
        });
    });
    return proxy;
}

/**
 * @param {object} target - the target object to proxy
 * @param {object} options - options object for Proxier
 * @param {boolean} options.preserveEvents - (optional) events on all objects are shared and handlers will
 *      only execute on the proxied object
 * @param {boolean} options.forcePrototype - (optional) force the prototype of the proxy to any new target
 *      so `instanceof` of the proxy will always match the object being proxied.
 *      NOTE: only do when necessary because it is computationally expensive to do.
 */
var Proxier = function (target, options) {
    options = options || {};
    this.__preserveEvents = options.preserveEvents;
    this.__forcePrototype = options.forcePrototype;
    this.__proxySwitch(target);
};

_.extend(Proxier.prototype, {
    __proxySwitch(target) {
        var prevEvents;
        var listenId;
        var listenKey;
        if (this.__preserveEvents) {
            prevEvents = this._events || undefined;
            if (this._listenerId) {
                listenKey = '_listenerId';
                listenId = this._listenerId;
            } else if (this._listenId) {
                listenKey = '_listenId';
                listenId = this._listenId;
            }
        }
        bindProxy(this, target, this.__forcePrototype);
        this._events = prevEvents;
        if (listenKey && listenId) {
            this[listenKey] = listenId;
        }
        return this;
    }
});

module.exports = Proxier;



// WEBPACK FOOTER //
// ./~/bunsen/helpers/Proxier.es.js